import { createSelector } from "reselect";

import {
  GlobalState,
  ShopifyProducts,
  ShopifyProductOption,
  ContentfulProducts,
  ContentfulProduct,
  Products,
  Variant
} from "types";

export default createSelector(
  (state: GlobalState): ShopifyProducts | null =>
    state.products.shopifyProducts,
  (state: GlobalState): ContentfulProducts | null =>
    state.products.contentfulProducts,
  (
    shopifyProducts: ShopifyProducts | null,
    contentfulProducts: ContentfulProducts | null
  ): Products | null =>
    contentfulProducts &&
    shopifyProducts &&
    contentfulProducts.reduce(
      (sanitizedProducts: Products, contentfulProduct: ContentfulProduct) => {
        const productHandle = contentfulProduct.productHandle;
        const shopifyProduct = shopifyProducts[contentfulProduct.productHandle];
        const variantOptions = shopifyProduct.options.filter(
          (option: ShopifyProductOption) => option.values.length !== 1
        );
        const variants = shopifyProduct.variants.map(shopifyVariant => {
          const sanitizedShopifyVariant: Variant = {
            variantId: shopifyVariant.id,
            availableForSale: shopifyVariant.availableForSale,
            price: shopifyVariant.price,
            product: shopifyVariant.product,
            variantOptions: shopifyVariant.variantOptions,
            title: shopifyVariant.title,
            imageIndex: 0
          };

          contentfulProduct.variants.forEach((contentfulVariant, i) => {
            const productsVariantOptionIsMatching = contentfulVariant.associatedVariantValues.every(
              (variant: string) =>
                shopifyVariant.variantOptions.includes(variant)
            );

            if (productsVariantOptionIsMatching) {
              sanitizedShopifyVariant.imageIndex = i;
            }
          });

          return sanitizedShopifyVariant;
        });

        let possibleOptionCombinations: string[][] = [];
        let currentIndex = 0;

        variantOptions.forEach((option, i) => {
          let sortedCurrentOptionValues: string[][] = [];

          option.values.forEach(value => {
            sortedCurrentOptionValues.push([value]);

            if (currentIndex !== i) {
              const getPossibleCombination = possibleOptionCombinations.map(
                option => option.concat([value])
              );
              sortedCurrentOptionValues = sortedCurrentOptionValues.concat(
                getPossibleCombination
              );
            }
          });

          possibleOptionCombinations = possibleOptionCombinations.concat(
            sortedCurrentOptionValues
          );
          currentIndex = i;
        });

        const notAvailableOptions = possibleOptionCombinations.reduce(
          (sanitizedOptions: string[][], options: string[]) => {
            const isNotAvailableOption = variants.every(variant => {
              if (
                options.every(option => variant.variantOptions.includes(option))
              ) {
                return !variant.availableForSale;
              }

              return true;
            });

            if (isNotAvailableOption) {
              sanitizedOptions.push(options);
            }

            return sanitizedOptions;
          },
          []
        );

        const images = contentfulProduct.variants.map(
          variant => variant.images
        );

        sanitizedProducts[productHandle] = {
          id: shopifyProduct.id,
          title: contentfulProduct.title,
          care: contentfulProduct.care,
          fit: contentfulProduct.fit,
          details: contentfulProduct.details,
          stickerLabel: contentfulProduct.productStickerLabel,
          colorVariantsInfo: contentfulProduct.colorVariantsInfo,
          prefix: contentfulProduct.prefix,
          moreProducts: contentfulProduct.moreProducts,
          variantOptions,
          images,
          variants,
          productHandle,
          notAvailableOptions
        };

        return sanitizedProducts;
      },
      {}
    )
);
